import React from "react";
import {
    Paperclip,
    Upload,
    Loader2,
    FileText,
    Image as ImageIcon,
    ChevronDown,
    X,
} from "lucide-react";

// Quote attachments panel — extra pages (images / PDFs) appended to
// the printed quote. Extracted from QuoteView.jsx (iter-102 refactor).
// Stateless: all upload + reorder logic lives in the parent, this
// component just renders.
//
// Props:
//   attachments         array of {id, name, kind: 'image'|'pdf', size}
//   canEditLines        boolean — gates the upload/reorder/remove buttons
//   uploadingAttach     boolean — true while an upload is in flight
//   attachToTemplate    boolean — checkbox state
//   onToggleSaveWithTemplate (bool) => void
//   onUploadFile        (Event) => void — file input change handler
//   onMove              (id, dir: -1|1) => void
//   onRemove            (id) => void
//   inputRef            ref for the hidden file input (so the parent can
//                       trigger .click() programmatically)
export function QuoteAttachmentsPanel({
    attachments,
    canEditLines,
    uploadingAttach,
    attachToTemplate,
    onToggleSaveWithTemplate,
    onUploadFile,
    onMove,
    onRemove,
    inputRef,
}) {
    return (
        <div
            className="p-3 sm:p-4 bg-[#0A1128] border border-[#2A3655] rounded-sm space-y-3"
            data-testid="quote-attachments-panel"
        >
            <div className="flex flex-wrap items-center gap-2">
                <Paperclip className="w-4 h-4 text-[#00A3FF] shrink-0" />
                <h3 className="text-xs font-bold uppercase tracking-wider text-white">
                    Extra pages
                </h3>
                <span className="text-[10px] text-slate-500">
                    Images / PDFs · max 8 · 15 MB each
                </span>
                <div className="flex-1" />
                <label
                    className="flex items-center gap-1.5 text-[11px] text-slate-300 hover:text-white cursor-pointer select-none"
                    data-testid="quote-attach-to-template-toggle"
                >
                    <input
                        type="checkbox"
                        checked={attachToTemplate}
                        onChange={(e) => onToggleSaveWithTemplate(e.target.checked)}
                        className="accent-[#00A3FF]"
                    />
                    Save with template
                </label>
                <input
                    ref={inputRef}
                    type="file"
                    accept="image/*,application/pdf,.pdf"
                    onChange={onUploadFile}
                    className="hidden"
                    data-testid="quote-attachment-input"
                />
                <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    disabled={
                        !canEditLines
                        || uploadingAttach
                        || attachments.length >= 8
                    }
                    data-testid="quote-attachment-upload-btn"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-sm bg-[#007AFF] hover:bg-[#3395FF] text-white font-bold transition-colors disabled:opacity-50"
                >
                    {uploadingAttach ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                        <Upload className="w-3 h-3" />
                    )}
                    {uploadingAttach ? "Uploading…" : "Upload image / PDF"}
                </button>
            </div>
            {attachments.length === 0 ? (
                <p
                    className="text-[11px] text-slate-500 italic"
                    data-testid="quote-attachments-empty"
                >
                    No extra pages yet. Upload disclaimers, terms, warranty
                    cards, or photos and they'll print after the line items.
                </p>
            ) : (
                <ul className="space-y-1.5" data-testid="quote-attachments-list">
                    {attachments.map((a, i) => (
                        <li
                            key={a.id}
                            className="flex items-center gap-2 px-2 py-1.5 rounded-sm bg-[#0F1B3A] border border-[#1C253C]"
                            data-testid={`quote-attachment-${a.id}`}
                        >
                            <span className="text-[10px] tabular-nums text-slate-500 w-5 text-right">
                                {i + 1}
                            </span>
                            {a.kind === "image" ? (
                                <ImageIcon className="w-3.5 h-3.5 text-[#00C853] shrink-0" />
                            ) : (
                                <FileText className="w-3.5 h-3.5 text-[#FF6B6B] shrink-0" />
                            )}
                            <span className="text-xs text-slate-200 truncate flex-1">
                                {a.name}
                            </span>
                            <span className="text-[10px] text-slate-500 tabular-nums shrink-0 hidden sm:inline">
                                {(a.size / 1024).toFixed(0)} KB
                            </span>
                            <div className="flex items-center gap-0.5">
                                <button
                                    type="button"
                                    onClick={() => onMove(a.id, -1)}
                                    disabled={!canEditLines || i === 0}
                                    className="p-1 rounded-sm text-slate-400 hover:text-white hover:bg-[#1C253C] disabled:opacity-30 disabled:cursor-not-allowed"
                                    aria-label="Move up"
                                    data-testid={`quote-attachment-${a.id}-up`}
                                >
                                    <ChevronDown className="w-3 h-3 rotate-180" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => onMove(a.id, 1)}
                                    disabled={
                                        !canEditLines
                                        || i === attachments.length - 1
                                    }
                                    className="p-1 rounded-sm text-slate-400 hover:text-white hover:bg-[#1C253C] disabled:opacity-30 disabled:cursor-not-allowed"
                                    aria-label="Move down"
                                    data-testid={`quote-attachment-${a.id}-down`}
                                >
                                    <ChevronDown className="w-3 h-3" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => onRemove(a.id)}
                                    disabled={!canEditLines}
                                    className="p-1 rounded-sm text-slate-400 hover:text-red-300 hover:bg-red-500/15 disabled:opacity-30"
                                    aria-label="Remove"
                                    data-testid={`quote-attachment-${a.id}-remove`}
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
